// Clip in homogeneous camera space, before the perspective divide. Keeping the
// original clip coordinates is essential when a face or rail crosses the eye.
const EPSILON=1e-10;
const PLANES=[
 p=>p.clipZ+p.clipW, // near
 p=>p.clipW-p.clipZ, // far
 p=>p.clipX+p.clipW,
 p=>p.clipW-p.clipX,
 p=>p.clipY+p.clipW,
 p=>p.clipW-p.clipY,
];
const finite=p=>p&&p.valid!==false&&['clipX','clipY','clipZ','clipW'].every(k=>Number.isFinite(p[k]));
const outcode=p=>(p.clipZ+p.clipW<0?1:0)|(p.clipW-p.clipZ<0?2:0)|(p.clipX+p.clipW<0?4:0)|(p.clipW-p.clipX<0?8:0)|(p.clipY+p.clipW<0?16:0)|(p.clipW-p.clipY<0?32:0);
const lerp=(a,b,t)=>{
 const p={};
 // Numeric attributes (for example texture u/v) travel with clipped vertices.
 for(const key of Object.keys(a))if(typeof a[key]==='number'&&typeof b[key]==='number')p[key]=a[key]+(b[key]-a[key])*t;
 p.valid=true;return p;
};
function screenPoint(p,width,height,bounded=false){
 const w=p.clipW,front=w>EPSILON,denominator=Math.abs(w)>EPSILON?w:1;
 let x=(p.clipX/denominator*.5+.5)*width,y=(-p.clipY/denominator*.5+.5)*height,z=p.clipZ/denominator;
 if(!Number.isFinite(x))x=width*.5;if(!Number.isFinite(y))y=height*.5;if(!Number.isFinite(z))z=2;
 if(bounded){x=Math.max(0,Math.min(width,x));y=Math.max(0,Math.min(height,y));z=Math.max(-1,Math.min(1,z));}
 return{...p,x,y,z,visible:finite(p)&&front&&p.clipZ+p.clipW>=-EPSILON&&p.clipW-p.clipZ>=-EPSILON};
}
/** Perspective projection with finite screen coordinates and retained clip data.
 * visible means in front of the near plane and before far, not inside the viewport:
 * a sprite with an off-screen anchor may still overlap the image.
 */
export function projectCanvasPoint(camera,x,y,z,width,height){
 const v=camera.matrixWorldInverse.elements,p=camera.projectionMatrix.elements,
 vx=v[0]*x+v[4]*y+v[8]*z+v[12],vy=v[1]*x+v[5]*y+v[9]*z+v[13],vz=v[2]*x+v[6]*y+v[10]*z+v[14],vw=v[3]*x+v[7]*y+v[11]*z+v[15];
 const clip={clipX:p[0]*vx+p[4]*vy+p[8]*vz+p[12]*vw,clipY:p[1]*vx+p[5]*vy+p[9]*vz+p[13]*vw,clipZ:p[2]*vx+p[6]*vy+p[10]*vz+p[14]*vw,clipW:p[3]*vx+p[7]*vy+p[11]*vz+p[15]*vw,cameraDepth:-vz};
 clip.valid=finite(clip);return screenPoint(clip,width,height);
}
/** Sutherland-Hodgman clipping also retains interpolated numeric vertex attributes. */
export function clipCanvasPolygon(points,width,height){
 if(points.length<3||!points.every(finite))return[];
 let any=0,all=63;for(const p of points){const code=outcode(p);any|=code;all&=code;}if(all)return[];
 if(!any)return points.map(p=>screenPoint(p,width,height,true));
 let result=points;
 for(const distance of PLANES){
  const output=[];let a=result[result.length-1],da=a?distance(a):0;
  for(const b of result){const db=distance(b),insideA=da>=0,insideB=db>=0;
   if(insideA!==insideB)output.push(lerp(a,b,da/(da-db)));
   if(insideB)output.push(b);a=b;da=db;
  }
  result=output;if(result.length<3)return[];
 }
 return result.map(p=>screenPoint(p,width,height,true));
}
/** Each visible source edge becomes an independent segment; hidden gaps never join. */
export function clipCanvasPolyline(points,width,height){
 const segments=[];
 for(let i=1;i<points.length;i++){
  const a=points[i-1],b=points[i];if(!finite(a)||!finite(b))continue;
  let from=0,to=1,rejected=false;
  for(const distance of PLANES){const da=distance(a),db=distance(b);
   if(da<0&&db<0){rejected=true;break;}
   if((da<0)!==(db<0)){const t=da/(da-db);if(da<0)from=Math.max(from,t);else to=Math.min(to,t);}
  }
  if(rejected||from>to)continue;
  const start=from>0?lerp(a,b,from):a,end=to<1?lerp(a,b,to):b;
  segments.push([screenPoint(start,width,height,true),screenPoint(end,width,height,true)]);
 }
 return segments;
}

/** Keep a billboard's measuring axis from flipping if that axis crosses the eye.
 * Its root must remain in front; only the scale uses the local projection tangent.
 */
export function canvasSpriteOffset(anchor,end,width,height){
 if(anchor.visible===false)return{x:0,y:0};
 if(end.visible!==false)return{x:end.x-anchor.x,y:end.y-anchor.y};
 const w=anchor.clipW;
 if(!(w>EPSILON)||!finite(anchor)||!finite(end))return{x:0,y:0};
 const dw=end.clipW-w;
 return{x:((end.clipX-anchor.clipX)*w-anchor.clipX*dw)/(w*w)*width*.5,y:-((end.clipY-anchor.clipY)*w-anchor.clipY*dw)/(w*w)*height*.5};
}

/** Upright sprite size at a single camera depth. Project the world-up direction
 * into the image plane without a perspective-depth derivative: the latter grows
 * as 1/depth² for offscreen roots and can flood a close view with a distant plant.
 * A billboard remains a flat image, so its measuring axis must not cross the eye.
 */
export function canvasUprightSpriteUnit(camera,anchor,width,height){
 if(anchor.visible===false||!(anchor.cameraDepth>EPSILON))return 0;
 const v=camera.matrixWorldInverse.elements,p=camera.projectionMatrix.elements;
 const unit=Math.hypot(p[0]*v[4]*width,p[5]*v[5]*height)/(2*anchor.cameraDepth);
 return Number.isFinite(unit)?unit:0;
}

/** Project a world direction in the anchor's image plane, at constant depth. */
export function canvasSpriteDirection(camera,anchor,dx,dy,dz,width,height){
 if(anchor.visible===false||!(anchor.cameraDepth>EPSILON))return{x:0,y:0};
 const v=camera.matrixWorldInverse.elements,p=camera.projectionMatrix.elements,
 vx=v[0]*dx+v[4]*dy+v[8]*dz,vy=v[1]*dx+v[5]*dy+v[9]*dz;
 return{x:p[0]*vx/anchor.cameraDepth*width*.5,y:-p[5]*vy/anchor.cameraDepth*height*.5};
}

/** Conservative sprite bounds, expressed in multiples of its world-unit scale. */
export function canvasSpriteInView(anchor,unit,width,height,left=-1,right=1,top=-1,bottom=1){
 if(anchor.visible===false||!Number.isFinite(unit)||unit<=0)return false;
 // Affine test renderers omit viewport dimensions and intentionally draw all.
 if(!(width>0&&height>0))return true;
 return anchor.x+right*unit>=0&&anchor.x+left*unit<=width&&anchor.y+bottom*unit>=0&&anchor.y+top*unit<=height;
}
